import { i18n } from '@lingui/core';
import { type FC, type ReactNode, useEffect, useRef } from 'react';
import { useAsync } from 'react-use';
import { useMe } from '@/account/profile/api';
import { useHasAccessToken } from '@/auth/api';
import { ClientOnly } from '@/shared/ClientOnly';
import { initiateChatwoot } from './utils';
import './style.css';

export * from './utils';

const RawChatwootProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { value: chatwoot } = useAsync(initiateChatwoot);
  const me = useMe();
  const hasAccessToken = useHasAccessToken();
  const lastIdentifier = useRef<string>('');

  useEffect(() => {
    if (!chatwoot) return;
    chatwoot.setLocale(i18n.locale);
    return i18n.on('change', () => chatwoot.setLocale(i18n.locale));
  }, [chatwoot]);

  useEffect(() => {
    if (!chatwoot) return;
    if (me.data?.external_id) {
      // `username` is the fallback because Chatwoot rejects a user object
      // without at least one of name / email / avatar_url.
      chatwoot.setUser(me.data.external_id, {
        name: me.data.full_name || me.data.username,
        email: me.data.email ?? undefined,
        phone_number: me.data.phone_number ?? undefined,
      });
      lastIdentifier.current = me.data.external_id;
    } else if (lastIdentifier.current && !hasAccessToken) {
      // Clear the identified session on logout, otherwise the next person on
      // this browser inherits the previous user's conversation history.
      chatwoot.reset();
      lastIdentifier.current = '';
    }
  }, [
    chatwoot,
    me.data?.external_id,
    me.data?.full_name,
    me.data?.username,
    me.data?.email,
    me.data?.phone_number,
    hasAccessToken,
  ]);

  return <>{children}</>;
};

export const ChatwootProvider: typeof RawChatwootProvider = ({ children }) => (
  <ClientOnly fallback={children}>
    <RawChatwootProvider>{children}</RawChatwootProvider>
  </ClientOnly>
);
